import axios from "axios";

const FALLBACK_API = "http://localhost:10000/api";

let API_BASE_URL = FALLBACK_API;

try {
  if (
    typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_API_URL
  ) {
    API_BASE_URL = process.env.REACT_APP_API_URL;
  }
} catch (e) {
  API_BASE_URL = FALLBACK_API;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Global request interceptor - Add auth token and branch_id
api.interceptors.request.use(
  (config) => {
    // Always get fresh token and branch_id from localStorage to avoid stale state
    const token = localStorage.getItem('token');
    const branchId = localStorage.getItem('branch_id') || localStorage.getItem('secure_branch_id');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Store token in config for later verification in response interceptor
      config._token = token;
    }
    
    // Add branch_id to headers as fallback (auth middleware checks headers)
    if (branchId) {
      config.headers['X-Branch-Id'] = String(branchId);
      
      // Also add to request data/params based on method
      if (config.method === 'get' || config.method === 'delete') {
        config.params = { ...config.params, branch_id: branchId };
      } else if (config.data && typeof config.data === 'object') {
        // Add branch_id to request body for POST/PUT requests
        config.data = { ...config.data, branch_id: branchId };
      }
    }
    
    const finalUrl = `${config.baseURL || ''}${config.url || ''}`;
    console.log(`[API] ${String(config.method || 'GET').toUpperCase()} → ${finalUrl}`);
    console.log(`[API] Token attached:`, !!token);
    console.log(`[API] Branch ID attached:`, !!branchId);
    console.log(`[API] Base URL:`, config.baseURL);
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response interceptor - Handle errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses (skip logging for blob responses)
    if (response.config.responseType !== 'blob') {
      console.log(`[API] ← ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    if (error.response) {
      console.log(`[API] ← ${error.response.status} ${error.config?.url}`);
      
      // Don't try to log blob data as JSON
      if (error.config?.responseType === 'blob') {
        // For blob errors, try to read the blob as text to see error message
        if (error.response.data instanceof Blob) {
          error.response.data.text().then(text => {
            try {
              const jsonError = JSON.parse(text);
              console.error('[API] Error:', jsonError);
            } catch (e) {
              console.error('[API] Blob Error (could not parse):', text.substring(0, 100));
            }
          }).catch(() => {
            console.error('[API] Blob Error (could not read)');
          });
        } else {
          console.error('[API] Error:', error.response.data);
        }
      } else {
        console.error('[API] Error:', error.response.data);
      }
      
      // Handle authentication errors (401) - Smart logout handling
      // Note: 403 errors are handled separately as they may be permission-based, not auth failures
      if (error.response.status === 401) {
        const url = error.config?.url || '';
        const currentPath = window.location.pathname;
        const now = Date.now();
        const storedLoginTime = localStorage.getItem('last_login_time');
        const timeSinceLogin = storedLoginTime ? now - parseInt(storedLoginTime) : Infinity;
        
        // Don't redirect if it's a login/auth endpoint (to avoid redirect loop)
        const isAuthEndpoint = url.includes('/auth/login') || 
                               url.includes('/auth/register') || 
                               url.includes('/auth/me');
        
        // Check if token exists
        const hasToken = localStorage.getItem('token');
        
        // NEVER logout if:
        // 1. Auth endpoint (login/register)
        // 2. Just logged in (within last 20 seconds) - initial API calls might fail
        // 3. Already on login page
        // 4. No token exists (might be public endpoint)
        const isInitialLoad = timeSinceLogin < 20000; // 20 seconds grace period (increased)
        const isLoginPage = currentPath === '/login' || currentPath === '/';
        
        console.log('[API] 🔍 401 Error Analysis:', {
          url,
          currentPath,
          timeSinceLogin: Math.round(timeSinceLogin / 1000) + 's',
          isAuthEndpoint,
          isInitialLoad,
          isLoginPage,
          hasToken: !!hasToken
        });
        
        if (isAuthEndpoint || isInitialLoad || isLoginPage || !hasToken) {
          if (isInitialLoad && hasToken) {
            console.log('[API] ⚠️ 401 received shortly after login - ignoring (grace period)', {
              timeSinceLogin: Math.round(timeSinceLogin / 1000) + 's',
              remainingGracePeriod: Math.round((20000 - timeSinceLogin) / 1000) + 's'
            });
          }
          return Promise.reject(error);
        }
        
        // Real auth failure - logout
        console.log('[API] ⚠️ Authentication failed - clearing token and redirecting to login', {
          url,
          currentPath,
          timeSinceLogin: Math.round(timeSinceLogin / 1000) + 's'
        });
        
        // Clear all auth-related data from localStorage
        localStorage.removeItem("token");
        localStorage.removeItem("clientToken");
        localStorage.removeItem("client");
        localStorage.removeItem("userData");
        localStorage.removeItem("last_login_time");
        
        // Redirect to login page
        if (currentPath !== '/login') {
          window.location.href = '/login';
        }
      } else if (error.response.status === 403) {
        // 403 Forbidden - Permission denied, not authentication failure
        // Don't logout, just log and reject (components can handle it)
        const url = error.config?.url || '';
        console.log('[API] ⚠️ 403 Forbidden - Permission denied', {
          url,
          message: error.response?.data?.message || 'Access denied'
        });
        // Don't redirect or logout - just reject so component can handle
        return Promise.reject(error);
      }
    } else if (error.request) {
      // Network error - no response received
      console.error('[API] Network Error:', {
        message: error.message,
        code: error.code,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        fullURL: `${error.config?.baseURL || ''}${error.config?.url || ''}`,
      });
      
      // If it's a network error in production, suggest checking backend
      if (typeof window !== 'undefined' && window.location) {
        const origin = window.location.origin;
        if (!origin.includes('localhost') && !origin.includes('127.0.0.1')) {
          console.error('[API] ⚠️ Network error in production. Check if backend server is running.');
          console.error('[API] Current API URL:', error.config?.baseURL);
          console.error('[API] Full request URL:', `${error.config?.baseURL || ''}${error.config?.url || ''}`);
          console.error('[API] Error message:', error.message);
          console.error('[API] Error code:', error.code);
        }
      }
    } else {
      console.error('[API] Request Setup Error:', error.message);
    }
    // Always reject the promise so components can handle errors
    return Promise.reject(error);
  }
);

export default api;
